import * as React from 'react';
import { CheckIcon, ChevronDownIcon, X, CheckCheck } from 'lucide-react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@/lib/utils';
import { useFieldLocked } from './FieldLock';

export interface SearchableMultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  subtitle?: string;
}

export interface SearchableMultiSelectProps {
  /** Id for the trigger button, so a `<label htmlFor>` can name it. */
  id?: string;
  options: SearchableMultiSelectOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  highlightValues?: Set<string>;
  activeHighlight?: boolean;
  /**
   * Fires when the panel opens or closes. Lets a caller treat the panel as a
   * staging area — collect a selection while it is open, act on it once on
   * close — instead of reacting to every individual toggle.
   */
  onOpenChange?: (open: boolean) => void;
}

export function SearchableMultiSelect({
  id,
  options,
  selectedValues,
  onSelectionChange: onSelectionChangeProp,
  placeholder = 'All',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No options found.',
  disabled: disabledProp = false,
  className,
  highlightValues,
  activeHighlight = false,
  onOpenChange: onOpenChangeProp,
}: SearchableMultiSelectProps) {
  /**
   * Inside a locked FieldLockScope the list still opens, so the picks can be
   * read (they sort first), but nothing in it changes them: no clear, no
   * Select All / Select N, a click on an option does nothing, and the trigger
   * is never drawn disabled.
   */
  const locked = useFieldLocked();
  const onSelectionChange = locked ? () => {} : onSelectionChangeProp;
  const onOpenChange = locked ? undefined : onOpenChangeProp;
  const disabled = !locked && disabledProp;
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const [focusedIndex, setFocusedIndex] = React.useState<number>(-1);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listboxRef = React.useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = React.useState<number | undefined>(undefined);

  const selectedSet = React.useMemo(() => new Set(selectedValues), [selectedValues]);

  React.useEffect(() => {
    if (open && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
      setTimeout(() => inputRef.current?.focus(), 0);
      setFocusedIndex(-1);
    } else {
      setSearchValue('');
      setFocusedIndex(-1);
    }
  }, [open]);

  React.useEffect(() => {
    if (focusedIndex >= 0 && listboxRef.current) {
      const option = listboxRef.current.children[focusedIndex] as HTMLElement;
      if (option) option.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [focusedIndex]);

  const filteredOptions = React.useMemo(() => {
    let result = options;
    if (searchValue) {
      const search = searchValue.toLowerCase();
      result = options.filter(
        (o) =>
          o.label.toLowerCase().includes(search) ||
          o.value.toLowerCase().includes(search) ||
          (o.subtitle && o.subtitle.toLowerCase().includes(search)),
      );
    }
    return [...result].sort((a, b) => {
      const aRank = selectedSet.has(a.value) ? 0 : highlightValues?.has(a.value) ? 1 : 2;
      const bRank = selectedSet.has(b.value) ? 0 : highlightValues?.has(b.value) ? 1 : 2;
      return aRank - bRank;
    });
  }, [options, searchValue, selectedSet, highlightValues]);

  const toggleOption = (optionValue: string) => {
    if (selectedSet.has(optionValue)) {
      onSelectionChange(selectedValues.filter((v) => v !== optionValue));
    } else {
      onSelectionChange([...selectedValues, optionValue]);
    }
  };

  const allOptionsSelected = options.length > 0 && options.every((o) => selectedSet.has(o.value));

  const toggleSelectAll = () => {
    if (allOptionsSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(options.filter((o) => !o.disabled).map((o) => o.value));
    }
  };

  const isSearching = searchValue.length > 0;
  const allFilteredSelected =
    isSearching &&
    filteredOptions.length > 0 &&
    filteredOptions.every((o) => selectedSet.has(o.value));

  const toggleSelectFiltered = () => {
    const filteredValues = new Set(
      filteredOptions.filter((o) => !o.disabled).map((o) => o.value),
    );
    if (allFilteredSelected) {
      onSelectionChange(selectedValues.filter((v) => !filteredValues.has(v)));
    } else {
      const merged = new Set([...selectedValues, ...filteredValues]);
      onSelectionChange([...merged]);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectionChange([]);
  };

  // Escape and Tab close the panel from inside the search field rather than
  // through Radix, so route every close through here — a caller staging a
  // selection until close must not miss those two exits.
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          const option = filteredOptions[focusedIndex];
          if (!option.disabled) toggleOption(option.value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        handleOpenChange(false);
        break;
      case 'Tab':
        handleOpenChange(false);
        break;
    }
  };

  const triggerLabel =
    selectedValues.length === 0 ? placeholder : `${selectedValues.length} selected`;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Trigger asChild>
        <button
          ref={triggerRef}
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn(
            'flex w-full min-w-0 items-center justify-between gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm transition-colors outline-none focus-visible:border-blue-400 focus-visible:ring-2 focus-visible:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-50 h-9',
            activeHighlight && selectedValues.length > 0 && 'border-blue-400 ring-1 ring-blue-100',
            className,
          )}
        >
          <span
            className={cn(
              'line-clamp-1 min-w-0 flex flex-1 items-center gap-2 truncate',
              selectedValues.length === 0
                ? 'text-slate-400'
                : activeHighlight
                  ? 'text-blue-700 font-medium'
                  : 'text-slate-900',
            )}
          >
            {triggerLabel}
          </span>
          <div className="flex items-center gap-1">
            {selectedValues.length > 0 && !disabled && !locked && (
              <span
                role="button"
                tabIndex={-1}
                onClick={handleClear}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onSelectionChange([]);
                  }
                }}
                className="rounded-sm opacity-50 hover:opacity-100 cursor-pointer inline-flex items-center justify-center"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <X className="h-4 w-4 text-slate-400" />
              </span>
            )}
            <ChevronDownIcon className="h-4 w-4 text-slate-400 shrink-0" />
          </div>
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          style={{
            minWidth: triggerWidth,
            maxHeight: 'var(--radix-popover-content-available-height, 400px)',
          }}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          // Escape closes this panel only. Stopped here, wherever focus sits
          // inside it, so it never reaches an enclosing Modal's document-level
          // Escape handler and closes the whole dialog (Select does the same).
          onEscapeKeyDown={(e) => e.stopPropagation()}
          className="z-50 rounded-md border border-slate-200 bg-white shadow-md overflow-hidden flex flex-col animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
        >
          <div className="flex flex-col min-h-0">
            <div className="border-b border-slate-200 px-3 shrink-0">
              <div className="flex items-center gap-1">
                <input
                  ref={inputRef}
                  type="text"
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    setFocusedIndex(-1);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={searchPlaceholder}
                  className="h-9 w-full bg-transparent py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none border-0 min-w-0 flex-1"
                />
                {isSearching && filteredOptions.length > 0 && !locked && (
                  <button
                    type="button"
                    onClick={toggleSelectFiltered}
                    className={cn(
                      'shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-medium whitespace-nowrap',
                      allFilteredSelected
                        ? 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200'
                        : 'bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200',
                    )}
                    title={
                      allFilteredSelected
                        ? 'Deselect searched results'
                        : 'Select all searched results'
                    }
                  >
                    <span className="flex items-center gap-0.5">
                      <CheckCheck className="h-3 w-3" />
                      {allFilteredSelected ? 'Deselect' : 'Select'} {filteredOptions.length}
                    </span>
                  </button>
                )}
              </div>
            </div>
            <div
              className="overflow-y-auto min-h-0"
              style={{
                touchAction: 'pan-y',
                maxHeight:
                  'min(400px, calc(var(--radix-popover-content-available-height, 400px) - 44px))',
              }}
              onWheel={(e) => e.stopPropagation()}
            >
              {filteredOptions.length === 0 ? (
                <div className="py-6 text-center text-sm text-slate-400">{emptyMessage}</div>
              ) : (
                <div ref={listboxRef} role="listbox" aria-multiselectable="true" aria-readonly={locked || undefined} className="p-1">
                  {!isSearching && options.length > 1 && !locked && (
                    <div
                      role="option"
                      aria-selected={allOptionsSelected}
                      onClick={toggleSelectAll}
                      className="relative flex w-full cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-none select-none border-b border-slate-200 mb-1 hover:bg-slate-50 font-medium"
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center rounded-sm border border-slate-400 shrink-0">
                        {allOptionsSelected && <CheckIcon className="h-3 w-3" />}
                      </span>
                      <span className="whitespace-nowrap">
                        {allOptionsSelected ? 'Deselect All' : 'Select All'}
                      </span>
                      <span className="text-xs text-slate-400 ml-auto">{options.length}</span>
                    </div>
                  )}
                  {filteredOptions.map((option, index) => {
                    const isSelected = selectedSet.has(option.value);
                    const isFocused = focusedIndex === index;
                    const isHighlighted = !isSelected && highlightValues?.has(option.value);
                    return (
                      <div
                        key={`${option.value}-${index}`}
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => !option.disabled && toggleOption(option.value)}
                        onMouseEnter={() => setFocusedIndex(index)}
                        className={cn(
                          'relative flex w-full cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-none select-none',
                          'hover:bg-slate-50',
                          isFocused && 'bg-slate-100',
                          isHighlighted && 'bg-amber-50 text-amber-900 border-l-2 border-l-amber-400',
                          option.disabled && 'pointer-events-none opacity-50',
                          locked && 'cursor-default',
                        )}
                      >
                        <span className="flex h-3.5 w-3.5 items-center justify-center rounded-sm border border-slate-400 shrink-0">
                          {isSelected && <CheckIcon className="h-3 w-3" />}
                        </span>
                        <span
                          className="whitespace-nowrap"
                          title={
                            option.subtitle
                              ? `${option.label} — ${option.subtitle}`
                              : option.label
                          }
                        >
                          {option.label}
                          {option.subtitle && (
                            <span className="ml-1.5 text-slate-400 font-normal">
                              {option.subtitle}
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
